import { useAbility } from '@casl/react';
import { Groups, LocationOn, MyLocation } from '@mui/icons-material';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { AbilityContext } from 'src/context/canContext';
import { useGetAllApplicationGroupsQuery } from 'src/services/switch-rest-api/application-group.service';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';

import { GROUPS_NAVIGATE_EVENT } from '../../../map/components/GroupsMap';

interface GroupsLocationItem {
  id: string;
  name: string;
  coordinates?: { latitude: number; longitude: number };
  address?: string | null;
}

interface GroupsLocationListWidgetProps {
  onItemClick?: () => void;
}

const GroupsLocationListWidget = ({
  onItemClick,
}: GroupsLocationListWidgetProps = {}) => {
  const theme = useTheme();
  const ability = useAbility(AbilityContext);
  const canReadGroups = ability.can(ACTIONS.READ, 'ApplicationGroups');

  const { data, isLoading, isFetching, isError } =
    useGetAllApplicationGroupsQuery(
      {
        limit: 0,
        orderByField: 'name',
      },
      {
        skip: !canReadGroups,
      }
    );

  const locationItems: GroupsLocationItem[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data
      .map((group) => {
        const coordinates = group.clientDetails?.address?.coordinates;
        if (coordinates?.length !== 2) return null;
        return {
          id: group._id,
          name: group.name,
          coordinates: {
            latitude: coordinates[1],
            longitude: coordinates[0],
          },
          address: group.clientDetails?.address?.address,
        };
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const handleItemClick = (item: GroupsLocationItem) => {
    if (!item.coordinates) return;

    const event = new CustomEvent(GROUPS_NAVIGATE_EVENT, {
      detail: {
        coordinates: item.coordinates,
        zoom: 12,
        itemId: item.id,
        itemType: 'applicationGroup',
        itemName: item.name,
        openPopup: true,
      },
    });
    window.dispatchEvent(event);
    onItemClick?.();
  };

  const renderSkeleton = () => (
    <>
      {[1, 2, 3, 4, 5].map((i) => (
        <ListItem key={i} disablePadding>
          <ListItemButton>
            <ListItemAvatar>
              <Skeleton variant='circular' width={40} height={40} />
            </ListItemAvatar>
            <ListItemText
              primary={<Skeleton variant='text' width='60%' />}
              secondary={<Skeleton variant='text' width='40%' />}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </>
  );

  const renderContent = () => {
    if (!canReadGroups) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            You do not have permission to view application groups.
          </Typography>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <List sx={{ width: '100%', overflow: 'auto' }}>{renderSkeleton()}</List>
      );
    }

    if (isError) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Unable to load application group locations.
          </Typography>
        </Box>
      );
    }

    if (!locationItems.length) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            py: 4,
            px: 2,
          }}
        >
          <LocationOn
            sx={{
              fontSize: 48,
              color: theme.tokens.colors.mutedForeground,
              opacity: 0.5,
            }}
          />
          <Typography variant='body2' color='text.secondary' textAlign='center'>
            No application groups with coordinates found.
          </Typography>
        </Box>
      );
    }

    return (
      <List
        sx={{
          width: '100%',
          overflow: 'auto',
          maxHeight: '100%',
          padding: 0,
          '& .MuiListItemButton-root:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& > :not(:last-child)': {
            borderBottom: `1px solid ${theme.tokens.colors.border}`,
          },
        }}
      >
        {locationItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              onClick={() => handleItemClick(item)}
              disabled={isFetching}
              sx={{ py: 0.5 }}
            >
              <ListItemAvatar>
                <Box
                  sx={{
                    height: 32,
                    width: 32,
                    borderRadius: '50%',
                    backgroundColor: theme.tokens.colors.card,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: theme.tokens.shadows.sm,
                  }}
                >
                  <Groups fontSize='small' color='primary' />
                </Box>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography
                    variant='body2'
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.name}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant='body1'
                    sx={{
                      color: theme.tokens.colors.mutedForeground,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}
                  >
                    {item.address ?? 'No address provided'}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <WidgetCard
      title='Group Locations'
      subheader='Quick select group by location'
      avatar={
        <Box
          sx={{
            padding: `${theme.tokens.spacing.sm}px`,
            height: `${theme.tokens.spacing.sm * 5}px`,
            width: `${theme.tokens.spacing.sm * 5}px`,
            mx: `-${theme.tokens.spacing.sm}px`,
            borderRadius: theme.tokens.borderRadius.full,
            background: theme.tokens.colors.card,
            boxShadow: theme.tokens.shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <MyLocation />
        </Box>
      }
      cardContentProps={{
        sx: {
          padding: '0px !important',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {renderContent()}
    </WidgetCard>
  );
};

export default GroupsLocationListWidget;
